import { Link } from "react-router";
import { useEndpoints } from "../../api/ApiContext";
import type { CampaignList, CampaignSummary } from "../../api/types";
import { CursorListView } from "../../components/CursorListView";
import { ButtonLink, Card, EmptyState, PageHeader, StatusPill, Table } from "../../components/ui";
import { formatLocal } from "../../lib/datetime";
import { useCursorList } from "../../lib/useCursorList";
import { CampaignStatePills } from "./campaignCopy";

// UX-DESIGN.md §2.4 list: GET /v1/campaigns (G-12, campaign_read.go:47),
// newest first, keyset-paginated. `states` is plural: every state that
// applies is shown, never one picked by precedence.

export function campaignName(c: { name: string | null; campaign_id: string }): string {
  return c.name ?? `Untitled campaign ${c.campaign_id.slice(0, 8)}`;
}

export function CampaignsPage() {
  const ep = useEndpoints();
  const list = useCursorList<CampaignList, CampaignSummary>(
    ["campaigns"],
    (cursor) => ep.advertiser.listCampaigns(cursor),
    (p) => p.campaigns,
  );

  return (
    <div className="stack-lg">
      <PageHeader
        title="Campaigns"
        description="Every campaign shows what's live and what's pending review as separate things."
        actions={<ButtonLink to="/advertiser/campaigns/new" variant="primary">New campaign</ButtonLink>}
      />

      <Card title="All campaigns">
        <CursorListView
          list={list}
          loadingLabel="Loading campaigns"
          empty={
            <EmptyState
              icon="megaphone"
              title="No campaigns yet"
              action={<ButtonLink to="/advertiser/campaigns/new">Build a campaign</ButtonLink>}
            >
              Build one in five steps: schedule, targeting, bid and budget, creative, then review and submit.
            </EmptyState>
          }
        >
          {(rows) => (
            <Table
              caption="Your campaigns, newest first"
              captionHidden
              rows={rows}
              rowKey={(c) => c.campaign_id}
              columns={[
                {
                  key: "name",
                  header: "Campaign",
                  cell: (c) => <Link to={`/advertiser/campaigns/${c.campaign_id}`}>{campaignName(c)}</Link>,
                },
                { key: "state", header: "Status", cell: (c) => <CampaignStatePills states={c.states} /> },
                {
                  key: "pending",
                  header: "Pending review",
                  cell: (c) =>
                    c.pending_candidate && c.active_release_version_id ? (
                      <StatusPill tone="info" icon="clock">
                        New version pending
                      </StatusPill>
                    ) : (
                      "—"
                    ),
                },
                {
                  key: "schedule",
                  header: "Schedule",
                  cell: (c) => (
                    <span className="small">
                      {formatLocal(c.schedule_start_utc)} → {c.schedule_end_utc ? formatLocal(c.schedule_end_utc) : "no end date"}
                    </span>
                  ),
                },
                { key: "model", header: "Pricing", cell: (c) => (c.pricing_model ? c.pricing_model.toUpperCase() : "—") },
                { key: "created", header: "Created", cell: (c) => formatLocal(c.created_at) },
              ]}
            />
          )}
        </CursorListView>
      </Card>
    </div>
  );
}
